// Platform → Rules: the numbers the service runs on.
//
// Two kinds of row, told apart on every tab. The rules a Super Admin can
// change here — trial length, the declined-card and deletion graces, the
// outbox caps, the API rate limit, the minimum password — each with the
// value in force, where it came from (saved here, the environment, or the
// code's default), its bounds, and every place it reaches, so a change is
// made knowing who feels it. And the rules that stay in code — throttles,
// token windows, job cadences, the upload ceilings — with their value and
// the file they live in, so the page is the whole picture and not only the
// part of it with a field. The Roles tab is the two ladders as the server
// enforces them.
//
// Every rung of the platform ladder can read this page (support answering
// "how long is the grace?" needs no Super Admin); only a Super Admin can
// save, and the server refuses everyone else. A change is validated whole —
// every number inside its bounds, the pairs that only make sense together
// still making sense — takes effect at once, and is written to the platform
// audit trail with what moved.
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SubTabs, TableCard, Toggle } from '../../components/fs';
import {
  EditableRule, FixedRule, PlatformRulesPage, RuleChange, getPlatformRules, resetPlatformRules, savePlatformRules,
} from '../../api/admin';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { ago } from '../admin/SsoShared';
import { usePlatform } from './PlatformLayout';
import { FormCard, SourceRow, StatusCard } from './PlatformStorage';

/** What the field holds while it is typed in: a string for a number, so a half-typed value is not a NaN. */
type Draft = Record<string, string | boolean>;

const draftOf = (rules: EditableRule[]): Draft =>
  Object.fromEntries(rules.map((r) => [r.key, r.kind === 'bool' ? !!r.value : String(r.value)]));

/** "14 days", "on" — a value as the toast and the trail say it. */
export function ruleValueText(rule: Pick<EditableRule, 'kind' | 'unit'>, v: number | boolean): string {
  if (rule.kind === 'bool') return v ? 'on' : 'off';
  return `${v} ${rule.unit}`;
}

/** The sentence a save's changes make: "First free trial 14 → 7 days, Deletion grace 30 → 45 days". */
export function changesSentence(rules: EditableRule[], changes: RuleChange[]): string {
  return changes.map((c) => {
    const r = rules.find((x) => x.key === c.key);
    if (!r) return `${c.key} ${String(c.from)} → ${String(c.to)}`;
    return r.kind === 'bool'
      ? `${r.label} ${c.to ? 'on' : 'off'}`
      : `${r.label} ${String(c.from)} → ${String(c.to)} ${r.unit}`;
  }).join(', ');
}

/**
 * The change a draft makes against the page: only the rules that differ,
 * each already checked against its bounds. The first problem is the error;
 * the server checks again, and the pairs, before anything is written.
 */
export function draftChanges(rules: EditableRule[], draft: Draft): { body: Record<string, number | boolean>; error: string | null } {
  const body: Record<string, number | boolean> = {};
  for (const r of rules) {
    const d = draft[r.key];
    if (d === undefined) continue;
    if (r.kind === 'bool') {
      if (!!d !== !!r.value) body[r.key] = !!d;
      continue;
    }
    const raw = String(d).trim();
    if (raw === '') return { body: {}, error: `${r.label} needs a number of ${r.unit}.` };
    const n = Number(raw);
    if (!Number.isInteger(n)) return { body: {}, error: `${r.label} must be a whole number of ${r.unit}.` };
    if (r.min !== undefined && n < r.min) return { body: {}, error: `${r.label} cannot be below ${r.min} ${r.unit}.` };
    if (r.max !== undefined && n > r.max) return { body: {}, error: `${r.label} cannot be above ${r.max} ${r.unit}.` };
    if (n !== r.value) body[r.key] = n;
  }
  return { body, error: null };
}

const SOURCE_WORD: Record<EditableRule['source'], string> = {
  platform: 'Saved here', env: 'Environment', default: 'Code default',
};

function RuleField({ rule, value, readOnly, onChange }: {
  rule: EditableRule;
  value: string | boolean;
  readOnly: boolean;
  onChange: (v: string | boolean) => void;
}) {
  const id = `rule-${rule.key}`;
  const tone = rule.source === 'default' ? 'off' : rule.source === 'env' ? 'warn' : 'ok';
  return (
    <div className="fs-field pl-rule">
      <label htmlFor={id}>{rule.label}</label>
      {rule.kind === 'bool' ? (
        <Toggle id={id} checked={!!value} disabled={readOnly} onChange={(c) => onChange(c)} label={value ? 'On' : 'Off'} />
      ) : (
        <div className="pl-rule-input">
          <input
            id={id} type="number" inputMode="numeric" min={rule.min} max={rule.max} step={1}
            value={String(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}
          />
          <span className="pl-rule-unit">{rule.unit}</span>
        </div>
      )}
      <div className="hint">{rule.blurb}</div>
      <div className="pl-rule-meta">
        <span className={`st-state ${tone}`}>{SOURCE_WORD[rule.source]}</span>
        <span>
          Default {ruleValueText(rule, rule.default)}
          {rule.kind === 'int' && rule.min !== undefined && rule.max !== undefined ? ` · ${rule.min}–${rule.max}` : ''}
          {rule.env ? ` · falls back to ${rule.env}` : ''}
        </span>
      </div>
      <div className="pl-rule-reaches">
        <span className="pl-rule-reaches-label">Reaches</span>
        <ul>{rule.reaches.map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
    </div>
  );
}

function FixedTable({ rows, title, hint }: { rows: FixedRule[]; title: string; hint: ReactNode }) {
  return (
    <TableCard title={title} hint={hint} className="pl-rules-fixed">
      <table className="fs-table pl-rules-table">
        <thead>
          <tr><th>Rule</th><th>Value</th><th>Lives in</th></tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.key}>
              <td>
                <div className="pl-rules-name">{f.label}</div>
                <div className="pl-rules-blurb">{f.blurb}</div>
              </td>
              <td className="pl-rules-value">{f.value}</td>
              <td className="pl-rules-where">
                <code>{f.where}</code>
                {f.env && <div className="pl-rules-env">env {f.env}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableCard>
  );
}

function LadderTable({ ladder, title, hint }: {
  ladder: PlatformRulesPage['roles']['platform']; title: string; hint: ReactNode;
}) {
  const label = (key: string) => ladder.roles.find((r) => r.key === key)?.label ?? key;
  return (
    <TableCard title={title} hint={hint} className="pl-rules-fixed"
      footer={<span className="st-muted">Enforced in <code>{ladder.where}</code>; a rung holds everything the rungs below it hold.</span>}>
      <table className="fs-table pl-rules-table">
        <thead><tr><th>May</th><th>Lowest rung</th></tr></thead>
        <tbody>
          {ladder.capabilities.map((c) => (
            <tr key={c.capability}><td><code>{c.capability}</code></td><td>{label(c.role)}</td></tr>
          ))}
        </tbody>
      </table>
    </TableCard>
  );
}

export default function PlatformRules() {
  const { role } = usePlatform();
  const superadmin = role === 'superadmin';
  const readOnly = !superadmin;
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<PlatformRulesPage | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const toast = useToast();

  const groups = page?.groups ?? [];
  const tabParam = params.get('tab');
  const tab = groups.some((g) => g.key === tabParam) ? tabParam! : (groups[0]?.key ?? 'billing');

  const { dirty, markSaved } = useDirty(draft, !!page);
  const apply = useCallback((p: PlatformRulesPage) => { setPage(p); setDraft(draftOf(p.rules)); }, []);

  async function onSave() {
    if (!page) return;
    const { body, error: problem } = draftChanges(page.rules, draft);
    if (problem) { setError(problem); throw new Error(problem); }
    setBusy(true); setError('');
    try {
      const r = await savePlatformRules(body);
      apply(r);
      markSaved();
      const said = changesSentence(r.rules, r.changes ?? []);
      toast(said ? `Rules saved and in force — ${said}.` : 'Nothing changed.', { kind: said ? 'ok' : 'info' });
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e; // "Save and leave" stays put rather than losing the edit
    } finally { setBusy(false); }
  }

  const guard = useUnsavedGuard({ dirty, what: 'Your changes to the platform rules', onSave: superadmin ? onSave : undefined });
  const { confirm } = guard;
  const goToTab = useCallback((t: string) => confirm(() => setParams({ tab: t }, { replace: true })), [confirm, setParams]);

  useEffect(() => {
    getPlatformRules().then(apply).catch((e) => setLoadError(e.message || 'Could not load the platform rules.'));
  }, [apply]);

  async function onReset() {
    if (!confirmReset) { setConfirmReset(true); return; }
    setConfirmReset(false); setBusy(true); setError('');
    try {
      const r = await resetPlatformRules();
      apply(r);
      markSaved();
      const said = changesSentence(r.rules, r.changes ?? []);
      toast(said ? `Every rule is back on the code’s default — ${said}.` : 'Every rule was already on the code’s default.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Reset failed.'); }
    finally { setBusy(false); }
  }

  const editable = useMemo(() => (page?.rules ?? []).filter((r) => r.group === tab), [page, tab]);
  const fixed = useMemo(() => (page?.fixed ?? []).filter((f) => f.group === tab), [page, tab]);
  const changedHere = editable.filter((r) => r.source !== 'default').length;
  const savedAnywhere = (page?.rules ?? []).some((r) => r.source === 'platform');
  const groupLabel = groups.find((g) => g.key === tab)?.label ?? '';

  return (
    <div className="pl-page pl-rules">
      <div className="fs-page-head pl-head">
        <div className="text">
          <h1>Platform rules</h1>
          <div className="fs-page-sub">The numbers the service runs on — what is in force, where it came from, and everything it reaches</div>
        </div>
      </div>

      {loadError && <div className="ad-err">{loadError}</div>}
      {!page && !loadError && <section className="fs-card"><div className="st-card-body st-muted">Loading…</div></section>}

      {page && (
        <>
          <SubTabs
            aria-label="Rules"
            className="pl-svc-tabs"
            items={groups.map((g) => ({ label: g.label, active: tab === g.key, onClick: () => goToTab(g.key) }))}
          />

          {readOnly && (
            <p className="pl-svc-readonly" role="note">
              Only a Super Admin can change these rules. Everyone on the platform ladder can read them.
            </p>
          )}

          {tab === 'roles' ? (
            <>
              <LadderTable ladder={page.roles.platform} title="The platform ladder"
                hint="What each rung of the people who run the service may do. Staff sees and supports; Admin changes customers and their billing; Super Admin changes the platform itself." />
              <LadderTable ladder={page.roles.account} title="A customer’s ladder"
                hint="Inside one account. Owner pays; Admin runs the workspace; Member does the work and sees what is shared with them. Platform staff hold no rung — they reach an account by masquerading, which is audited." />
            </>
          ) : (
            <>
              <StatusCard title={`${groupLabel} rules`}>
                <SourceRow
                  tone={changedHere ? 'warn' : 'ok'}
                  title={editable.length === 0
                    ? 'Every rule on this tab stays in code'
                    : changedHere
                      ? `${changedHere} of ${editable.length} changed from the code’s defaults`
                      : `Every rule here is the code’s default`}
                  blurb={page.updatedAt
                    ? `Rules last saved ${ago(page.updatedAt)}${page.updatedBy ? ` by ${page.updatedBy.email}` : ''}. A save takes effect at once here and within a minute on every other instance.`
                    : 'Nothing has been saved on this install: every editable rule is the code’s default, or the environment’s.'}
                />
                {tab === 'billing' && page.enforcement && (
                  <SourceRow
                    label="Paid plans"
                    tone={page.enforcement.enforcing ? 'ok' : page.enforcement.stripe ? 'warn' : 'off'}
                    title={page.enforcement.enforcing
                      ? '“A paid plan is paid for” is enforced'
                      : page.enforcement.stripe === 'test'
                        ? 'Not enforced: Stripe is on a test key'
                        : 'Not enforced: Stripe is not connected'}
                    blurb={page.enforcement.enforcing
                      ? `The billing sweep moves a paid plan nothing is paying for to Free, and a request on such an account already reads as Free (${page.enforcement.stripe === 'test' ? 'a test key, with the switch below on' : 'a live key'}).`
                      : page.enforcement.stripe === 'test'
                        ? 'With a test key plans are fixtures to test with. Turn the switch below on to enforce anyway — a staging install that should behave like production.'
                        : 'Without Stripe nothing can bill anyone, so nothing is enforced. Connect Stripe under Services.'}
                  />
                )}
              </StatusCard>

              {editable.length > 0 && (
                <FormCard title="Changed here" className="pl-rules-form">
                  {error && <div className="ad-err" role="alert">{error}</div>}
                  {editable.map((r) => (
                    <RuleField key={r.key} rule={r} value={draft[r.key] ?? (r.kind === 'bool' ? !!r.value : String(r.value))}
                      readOnly={readOnly || busy} onChange={(v) => setDraft((d) => ({ ...d, [r.key]: v }))} />
                  ))}
                  <div className="pl-svc-actions">
                    <button className="fs-btn primary" disabled={busy || readOnly || !dirty}
                      onClick={() => void onSave().catch(() => undefined)}>
                      {busy ? 'Working…' : 'Save rules'}
                    </button>
                    <button className="fs-btn neutral" disabled={busy || !dirty} onClick={() => { setDraft(draftOf(page.rules)); setError(''); }}>
                      Discard
                    </button>
                    <span className="pl-svc-spacer" />
                    {savedAnywhere && !readOnly && (
                      <button className="fs-btn danger sm" disabled={busy} onClick={() => void onReset()}>
                        {confirmReset ? 'Confirm — every rule back to the code’s default' : 'Reset all rules to defaults'}
                      </button>
                    )}
                  </div>
                  <p className="pl-svc-foot">
                    A change is checked whole before anything is written — every number inside its bounds, and the rules that only
                    make sense together still making sense — then recorded in the platform audit log with what moved.
                  </p>
                </FormCard>
              )}

              {fixed.length > 0 && (
                <FixedTable rows={fixed} title="Kept in code"
                  hint="Read at start-up or built once, so a change is a pull request. Shown here so the picture is whole." />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
